import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  CreditCard,
  FileText,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { useTranslation } from '../../../application/hooks/useTranslation';
import { usePaymentLock } from '../../../application/hooks/usePaymentLock';
import { formatDate } from '../../../domain/formatters/date';
import { InstallmentStatus, DelinquencyStatus } from '../../../domain/enums/SystemEnums';
import {
  TENOR,
  getInstallmentStatusSpec,
  type GridSlot,
} from '../../../domain/calculators/installments';
import { AmountDisplay } from '../ui/AmountDisplay';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { getSlotMonthLabel } from './installmentUtils';
import './installments.css';

/**
 * Solidified Month Detail Modal (DEVELOPMENT_PLAN.md §6.3 / Q2 / Q6 / Q7 / Q11 / Q12).
 * Strictly removes fabricated unit/sq-ft and milestone claims.
 * Pay affordance ONLY on payable rows; disabled for CLOSED accounts.
 */
export function MonthDetailModal({
  slot,
  delinquency,
  onClose,
}: {
  slot: GridSlot | null;
  delinquency?: DelinquencyStatus;
  onClose: () => void;
}) {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const { locked } = usePaymentLock();
  if (!slot) return null;

  const row = slot.row;
  const statusSpec = getInstallmentStatusSpec(row?.status, row?.dueDate);
  const statusLabel = t(statusSpec.labelKey);
  const monthName = getSlotMonthLabel(slot);

  const isClosed = delinquency === DelinquencyStatus.CLOSED;
  const isSettled = row?.status === InstallmentStatus.PAID;
  const isAdvanced = row?.status === InstallmentStatus.ADVANCED;
  const isUpcoming = row?.status === InstallmentStatus.UPCOMING && !statusSpec.isPayable;
  const isPayable = statusSpec.isPayable && !isClosed;

  const channel = isAdvanced
    ? t('installments.channelAdvance')
    : isSettled
      ? t('installments.channelCleared')
      : t('installments.channelOpen');

  return (
    <Modal
      open
      title={t('installments.monthOf', { n: slot.installmentNumber, total: TENOR })}
      onClose={onClose}
    >
      <div data-testid="month-modal" className="modal-voucher-wrap">
        {/* 1. Header Capsule */}
        <div className="modal-voucher-hero">
          <div className="modal-voucher-eyebrow">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Building2 size={14} color="#fed766" />
              Sristi Green Valley Ltd • Installment Record
            </span>
            <Badge tone={statusSpec.tone}>
              {statusLabel}
            </Badge>
          </div>

          <div className="modal-voucher-title-row">
            <div>
              <h3 className="modal-voucher-title">
                {t('installments.monthOf', { n: slot.installmentNumber, total: TENOR })}
                {monthName ? ` (${monthName})` : ''}
              </h3>
              <div className="modal-voucher-amount-box">
                <span className="modal-voucher-amount">
                  {row && Number.isFinite(row.amount) && !slot.invalidAmount ? (
                    <AmountDisplay amount={row.amount} />
                  ) : (
                    '—'
                  )}
                </span>
                {row && Number.isFinite(row.amount) && !slot.invalidAmount && (
                  <span className="modal-voucher-amount-label">
                    • Fixed Monthly Commitment
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 2. Detail Grid */}
        <div className="modal-voucher-grid">
          {/* Card A: Schedule & Verification Audit */}
          <div className="modal-voucher-card">
            <div className="modal-voucher-card-title">
              <Calendar size={13} color="var(--color-primary, #0d7c3d)" />
              Schedule Details
            </div>

            <div className="modal-voucher-row">
              <span className="modal-voucher-label">
                <Clock size={12} />
                {t('installments.dueDate')}
              </span>
              <span className="modal-voucher-val">
                {formatDate(row?.dueDate ?? null, locale) || '—'}
              </span>
            </div>

            <div className="modal-voucher-row">
              <span className="modal-voucher-label">
                <CheckCircle2 size={12} />
                {isAdvanced ? t('installments.prepaidDate') : t('installments.paidDate')}
              </span>
              <span className="modal-voucher-val">
                {row?.paidDate ? (
                  <span style={{ color: '#059669' }}>
                    {formatDate(row.paidDate, locale)}
                  </span>
                ) : (
                  <span className="muted">—</span>
                )}
              </span>
            </div>

            <div className="modal-voucher-row">
              <span className="modal-voucher-label">
                <CreditCard size={12} />
                {t('installments.channel')}
              </span>
              <span className="modal-voucher-val">{channel}</span>
            </div>

            <div className="modal-voucher-row">
              <span className="modal-voucher-label">
                <ShieldCheck size={12} />
                {t('installments.status')}
              </span>
              <span className="modal-voucher-val">
                <Badge tone={statusSpec.tone}>
                  {statusLabel}
                </Badge>
              </span>
            </div>
          </div>

          {/* Card B: Plan Reference */}
          <div className="modal-voucher-card">
            <div className="modal-voucher-card-title">
              <Building2 size={13} color="var(--color-primary, #0d7c3d)" />
              Plan Reference
            </div>

            <div className="modal-voucher-row">
              <span className="modal-voucher-label">
                <Sparkles size={12} />
                Tenure Plan
              </span>
              <span className="modal-voucher-val">120-Month Standard Tenor</span>
            </div>

            <div className="modal-voucher-row">
              <span className="modal-voucher-label">
                <Calendar size={12} />
                Tenure Year
              </span>
              <span className="modal-voucher-val">
                Year {Math.ceil(slot.installmentNumber / 12)} of 10
              </span>
            </div>

            <div className="modal-voucher-row">
              <span className="modal-voucher-label">
                <FileText size={12} />
                Reference
              </span>
              <span className="modal-voucher-val tabular" style={{ fontSize: '0.71875rem' }}>
                #SGVL-M{String(slot.installmentNumber).padStart(3, '0')}
              </span>
            </div>
          </div>
        </div>

        {/* 3. Contextual Action Deck */}
        <div className="modal-voucher-action-strip">
          {isPayable && (
            <div className="mg-modal-actions" style={{ marginTop: 0 }}>
              <Button
                data-testid="pay-installment"
                disabled={locked}
                title={locked ? t('payments.locked.short') : undefined}
                variant="primary"
                size="md"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => navigate('/payments')}
              >
                ⚡ {t('installments.payNow')}
              </Button>
            </div>
          )}

          {isSettled && (
            <div className="inst-modal-settled-strip">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <CheckCircle2 size={15} color="#059669" />
                <span>Payment Verified & Certified to Ledger</span>
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate('/payments')}
              >
                View Receipt →
              </Button>
            </div>
          )}

          {isAdvanced && (
            <div
              className="inst-modal-settled-strip"
              style={{
                background: '#f0fdfa',
                borderColor: '#99f6e4',
                color: '#0f766e',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 4,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                <Sparkles size={15} color="#0d9488" />
                <span>{monthName ? t('installments.advanceHint', { month: monthName }) : t('installments.advanced')}</span>
              </div>
              <span style={{ fontSize: '0.75rem', color: '#115e59' }}>
                {t('installments.advanceExplainer')}
              </span>
            </div>
          )}

          {isUpcoming && (
            <div className="inst-modal-upcoming-strip">
              <Clock size={15} color="#64748b" />
              <span>
                Scheduled future installment. Advance deposits can be made anytime from the deposit section.
              </span>
            </div>
          )}

          {isClosed && (
            <div className="inst-modal-upcoming-strip" style={{ color: '#dc2626', borderColor: '#fca5a5' }}>
              <ShieldCheck size={15} color="#dc2626" />
              <span>{t('installments.closedBannerDesc')}</span>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default MonthDetailModal;
